<template>
  <div class="px-6">
    <div v-if="$can('view-sales-status', 'Yacht')">
      <v-row
        align="center"
        justify="space-between"
        class="subtitle-1 pa-4"
        no-gutters
      >
        <v-col
          cols="auto"
        >
          Sales status
        </v-col>
        <v-col class="shrink">
          <v-chip
            :color="investmentStatusData[yacht.investment_status].color"
            dark
            label
            class="text-uppercase elevation-5"
            @click="revertInvestmentStatus"
          >
            {{ investmentStatusData[yacht.investment_status].label }}
          </v-chip>
        </v-col>
      </v-row>

      <draft
        v-if="$can('set-sales-status', 'Yacht') && yacht.investment_status === yachtStatuses.INVESTMENT_STATUS_DRAFT"
        @update:status="updateInvestmentStatus"
      />
      <sales
        v-if="$can('submit-for-approval', 'Yacht') && yacht.investment_status === yachtStatuses.INVESTMENT_STATUS_SALES"
        :yacht="yacht"
        @update:status="updateInvestmentStatus"
        @create:offer="$emit('create:offer')"
      />
      <approval
        v-if="$can('approve-business-deals', 'Yacht') && yacht.investment_status === yachtStatuses.INVESTMENT_STATUS_APPROVAL"
        :yacht="yacht"
        @approval:approve="updateApproval('approve')"
        @approval:reject="updateApproval('reject')"
      />
      <approved
        v-if="$can('set-reserved-status', 'Yacht') && yacht.investment_status === yachtStatuses.INVESTMENT_STATUS_APPROVED"
        :yacht="yacht"
        @update:status="updateInvestmentStatus"
      />
      <reserved
        v-if="$can('set-sold-status', 'Yacht') && yacht.investment_status === yachtStatuses.INVESTMENT_STATUS_RESERVED"
        :yacht="yacht"
        @update:status="updateInvestmentStatus"
      />

      <div
        v-if="yacht.sales_rep"
        class="px-4"
      >
        <v-row
          no-gutters
          class="mt-4 body-1 grey--text text--darken-2"
          align="center"
        >
          <v-col class="font-weight-medium">
            Current Sales Rep
          </v-col>
          <v-col
            cols="auto"
            class="font-weight-regular"
          >
            {{ yacht.sales_rep.name }}
          </v-col>
          <v-col
            v-if="yacht.editable_fields.indexOf('sales_rep') > -1"
            cols="auto"
            class="pl-2"
          >
            <v-icon
              color="primary"
              small
              @click="$emit('update:sales-rep')"
            >
              fa fa-pen
            </v-icon>
          </v-col>
        </v-row>
      </div>

      <v-card
        v-if="yacht.sales_slot"
        color="grey lighten-5"
        outlined
        class="mt-6"
        rounded="lg"
      >
        <v-card-text>
          <v-row
            v-if="yacht.sales_slot"
            class=" grey--text text--darken-1 body-1"
            no-gutters
            align="center"
          >
            <v-col>
              Sales Slot <strong>{{ yacht.sales_slot.yard_id }}</strong>
            </v-col>
            <v-col cols="auto">
              <v-chip
                small
                label
                :color="salesSlotStatusData[yacht.sales_slot.status].color"
                text-color="white"
                class="ma-0 caption"
              >
                {{ salesSlotStatusData[yacht.sales_slot.status].label }}
              </v-chip>
            </v-col>
          </v-row>
          <v-divider class="my-2" />
          <div v-if="yacht.sales_slot.last_order_date">
            Last Order / Production Start: <span class="font-weight-medium">{{ yacht.sales_slot.last_order_date | moment('D MMM YYYY') }}</span>
          </div>
          <div>Ex Factory: <span class="font-weight-medium">{{ yacht.sales_slot.ex_factory_date | moment('D MMM YYYY') }}</span></div>
          <div>Total Sales: <span class="font-weight-medium">{{ yacht.sales_slot.yachts_count }}</span></div>
          <v-alert
            v-if="salesSlotSoldCount > 0"
            outlined
            dense
            class="mt-1 body-1"
            type="error"
          >
            <strong>{{ salesSlotSoldCount }} yachts</strong> marked sold
          </v-alert>
          <v-alert
            v-if="salesSlotReservedCount > 0"
            outlined
            dense
            class="mt-1 body-1"
            type="warning"
          >
            <strong>{{ salesSlotReservedCount }} yachts</strong> marked reserved
          </v-alert>
          <v-divider class="my-2" />
          <div class="text-center">
            <v-btn
              text
              small
              class="text-none"
              color="primary"
              @click="yachtSalesSlotVisible = true"
            >
              View Details & Current Sales
              <v-icon
                right
                small
              >
                arrow_forward
              </v-icon>
            </v-btn>
          </div>
        </v-card-text>

        <yacht-sales-slot-view-dialog
          :sales-slot-id="yacht.sales_slot.id"
          :visible.sync="yachtSalesSlotVisible"
        />
      </v-card>

      <v-divider class="mt-6 mb-4" />
    </div>

    <v-row
      align="center"
      justify="space-between"
      class="subtitle-1 pa-4"
      no-gutters
    >
      <v-col>
        Logistics status
      </v-col>
      <v-col cols="auto">
        <v-menu>
          <template v-slot:activator="{ on }">
            <v-chip
              :color="statusData[yacht.status].color"
              dark
              label
              class="text-uppercase elevation-5"
              @click="yacht.status === yachtStatuses.STATUS_IN_USE ? undefined : revertLogisticsStatus()"
              v-on="yacht.status === yachtStatuses.STATUS_IN_USE ? on : undefined"
            >
              {{ statusData[yacht.status].label }}
            </v-chip>
          </template>
          <v-list dense>
            <v-list-item @click="revertLogisticsStatus">
              Revert Status
            </v-list-item>
            <v-divider />
            <v-list-item @click="updateStatus(yachtStatuses.STATUS_PHASED_OUT)">
              Phase Out
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <yacht-draft
      v-if="yacht.status === yachtStatuses.STATUS_DRAFT"
      :yacht="yacht"
      @update:status="updateStatus"
    />
    <pending-plan
      v-if="yacht.status === yachtStatuses.STATUS_PENDING"
      :yacht="yacht"
    />
    <delivery
      v-if="yacht.status === yachtStatuses.STATUS_DELIVERY || yacht.status === yachtStatuses.STATUS_PLANNED"
      :yacht="yacht"
      @update:status="updateStatus"
    />
    <in-use
      v-if="yacht.status === yachtStatuses.STATUS_IN_USE"
      :yacht="yacht"
    />
  </div>
</template>

<script>
import Yacht from '@/models/Yacht'
import Draft from '@/components/business/yachts/details/statusSections/sales/Draft'
import Sales from '@/components/business/yachts/details/statusSections/sales/Sales'
import Approval from '@/components/business/yachts/details/statusSections/sales/Approval'
import Approved from '@/components/business/yachts/details/statusSections/sales/Approved'
import Reserved from '@/components/business/yachts/details/statusSections/sales/Reserved'
import Delivery from '@/components/business/yachts/details/statusSections/yacht/Delivery'
import InUse from '@/components/business/yachts/details/statusSections/yacht/InUse'
import YachtDraft from '@/components/business/yachts/details/statusSections/yacht/Draft'
import PendingPlan from '@/components/business/yachts/details/statusSections/yacht/PendingPlan'
import YachtSalesSlotViewDialog from '@/components/business/yachts/YachtSalesSlotViewDialog'
import YachtSalesSlot from '@/models/YachtSalesSlot'

export default {
  name: 'StatusSection',
  components: {
    YachtSalesSlotViewDialog,
    PendingPlan,
    InUse,
    Delivery,
    Approval,
    Approved,
    Reserved,
    Sales,
    Draft,
    YachtDraft
  },
  props: {
    yacht: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      yachtStatuses: Yacht,
      statusData: Yacht.statusData,
      investmentStatusData: Yacht.investmentStatusData,
      yachtSalesSlotVisible: false
    }
  },
  computed: {
    salesSlotSoldCount() {
      if (!this.yacht.sales_slot || !this.yacht.sales_slot.yachts) return undefined

      return this.yacht.sales_slot.yachts.filter(y => y.investment_status === Yacht.INVESTMENT_STATUS_SOLD && y.id !== this.yacht.id).length
    },
    salesSlotReservedCount() {
      if (!this.yacht.sales_slot || !this.yacht.sales_slot.yachts) return undefined

      return this.yacht.sales_slot.yachts.filter(y => y.investment_status === Yacht.INVESTMENT_STATUS_RESERVED && y.id !== this.yacht.id).length
    }
  },
  created() {
    this.salesSlotStatusData = YachtSalesSlot.statusData
  },
  methods: {
    updateStatus(newStatus) {
      this.$emit('update:status', newStatus)
    },
    updateInvestmentStatus(newStatus) {
      this.$emit('update:investment-status', newStatus)
    },
    updateApproval(action) {
      this.$emit('approval', action)
    },
    revertInvestmentStatus() {
      if (!this.$can('revert-investment-status', 'Yacht') || this.yacht.investment_status === this.yachtStatuses.INVESTMENT_STATUS_DRAFT) return

      this.$emit('revert:investment-status')
    },
    revertLogisticsStatus: function () {
      if (!this.$can('revert-logistics-status', 'Yacht') || this.yacht.status === this.yachtStatuses.STATUS_DRAFT) return

      this.$emit('revert:logistics-status')
    }
  }
}
</script>

<style scoped>

</style>
